import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import {
  getProducts,
  getProductById,
  type ProductCategory,
  type ProductItem,
  type ProductSummary,
  type ProductPagination,
  type FetchProductsParams,
} from '../api/products'

interface ProductsState {
  summary: ProductSummary | null
  categories: ProductCategory[]
  products: ProductItem[]
  selectedProduct: any | null
  loadingProduct: boolean
  productError: string | null
  pagination: ProductPagination | null
  loading: boolean
  error: string | null
  selectedCategory: string
  searchQuery: string
  statusTab: 'Active' | 'Inactive' | 'All'
  currentPage: number
  limit: number
}

const initialState: ProductsState = {
  summary: null,
  categories: [],
  products: [],
  selectedProduct: null,
  loadingProduct: false,
  productError: null,
  pagination: null,
  loading: false,
  error: null,
  selectedCategory: 'cat_all',
  searchQuery: '',
  statusTab: 'All',
  currentPage: 1,
  limit: 25,
}

// Thunk to fetch products from GET /api/v1/erp/products
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params: FetchProductsParams | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { products: ProductsState }
      const currentParams: FetchProductsParams = {
        category_id: params?.category_id ?? state.products.selectedCategory,
        page: params?.page ?? state.products.currentPage,
        limit: params?.limit ?? state.products.limit,
        search: params?.search ?? state.products.searchQuery,
        status: params?.status ?? state.products.statusTab,
      }
      const response = await getProducts(currentParams)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch products')
    }
  }
)

// Thunk to fetch product details from GET /api/v1/erp/products/:id
export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await getProductById(id)
      return response.data || response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch product details')
    }
  }
)

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setSelectedCategory(state, action: PayloadAction<string>) {
      state.selectedCategory = action.payload
      state.currentPage = 1
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload
      state.currentPage = 1
    },
    setStatusTab(state, action: PayloadAction<'Active' | 'Inactive' | 'All'>) {
      state.statusTab = action.payload
      state.currentPage = 1
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload
      state.currentPage = 1
    },
    clearSelectedProduct(state) {
      state.selectedProduct = null
      state.loadingProduct = false
      state.productError = null
    },
    clearProductsState(state) {
      state.summary = null
      state.categories = []
      state.products = []
      state.selectedProduct = null
      state.loadingProduct = false
      state.productError = null
      state.pagination = null
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false
        state.summary = action.payload.summary
        state.categories = action.payload.categories || []
        state.products = action.payload.products || []
        state.pagination = action.payload.pagination
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loadingProduct = true
        state.productError = null
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loadingProduct = false
        state.selectedProduct = action.payload
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loadingProduct = false
        state.productError = action.payload as string
      })
  },
})

export const {
  setSelectedCategory,
  setSearchQuery,
  setStatusTab,
  setCurrentPage,
  setLimit,
  clearSelectedProduct,
  clearProductsState,
} = productsSlice.actions

export default productsSlice.reducer

